const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const ACTIVE_SOCIETY_KEY = 'activeSocietyId';
const SESSION_EXPIRES_AT_KEY = 'sessionExpiresAt';
const RECENT_SEARCHES_KEY = 'recentSearches';
const MAX_RECENT_SEARCHES = 5;

const getSessionDurationMs = () => {
  const configuredMinutes = Number(process.env.REACT_APP_SESSION_DURATION_MINUTES);
  const durationMinutes =
    Number.isFinite(configuredMinutes) && configuredMinutes > 0 ? configuredMinutes : 60;

  return durationMinutes * 60 * 1000;
};

const safeJsonParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getStoredSessionExpiresAt = () => {
  const expiresAt = Number(localStorage.getItem(SESSION_EXPIRES_AT_KEY));
  return Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt : null;
};

const isSessionExpired = () => {
  const expiresAt = getStoredSessionExpiresAt();
  return Boolean(expiresAt && expiresAt <= Date.now());
};

const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(SESSION_EXPIRES_AT_KEY);
};

const ensureActiveSession = () => {
  if (isSessionExpired()) {
    clearSession();
    return false;
  }

  return true;
};

export const storage = {
  getToken: () => {
    if (!ensureActiveSession()) {
      return null;
    }

    return localStorage.getItem(TOKEN_KEY);
  },
  setToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(SESSION_EXPIRES_AT_KEY, String(Date.now() + getSessionDurationMs()));
  },
  removeToken: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(SESSION_EXPIRES_AT_KEY);
  },

  getUser: () => safeJsonParse(localStorage.getItem(USER_KEY)),
  setUser: (user) => localStorage.setItem(USER_KEY, JSON.stringify(user)),
  removeUser: () => localStorage.removeItem(USER_KEY),
  getSessionExpiresAt: () => {
    if (!ensureActiveSession()) {
      return null;
    }

    return getStoredSessionExpiresAt();
  },

  getActiveSocietyId: () => localStorage.getItem(ACTIVE_SOCIETY_KEY) || null,
  setActiveSocietyId: (id) => {
    if (id) {
      localStorage.setItem(ACTIVE_SOCIETY_KEY, id);
    } else {
      localStorage.removeItem(ACTIVE_SOCIETY_KEY);
    }
  },
  removeActiveSocietyId: () => localStorage.removeItem(ACTIVE_SOCIETY_KEY),

  getRecentSearches: () => {
    const stored = safeJsonParse(localStorage.getItem(RECENT_SEARCHES_KEY));
    return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string') : [];
  },
  pushRecentSearch: (term) => {
    const trimmed = typeof term === 'string' ? term.trim() : '';
    if (!trimmed) {
      return [];
    }

    const existing = safeJsonParse(localStorage.getItem(RECENT_SEARCHES_KEY));
    const list = Array.isArray(existing) ? existing.filter((item) => typeof item === 'string') : [];
    const deduped = [trimmed, ...list.filter((item) => item.toLowerCase() !== trimmed.toLowerCase())];
    const capped = deduped.slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(capped));
    return capped;
  },
  clearRecentSearches: () => localStorage.removeItem(RECENT_SEARCHES_KEY),

  readSession: () => {
    if (!ensureActiveSession()) {
      return { token: null, user: null, expiresAt: null };
    }

    return {
      token: localStorage.getItem(TOKEN_KEY),
      user: safeJsonParse(localStorage.getItem(USER_KEY)),
      expiresAt: getStoredSessionExpiresAt(),
    };
  },

  persistSession: ({ token, user }) => {
    if (token) {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      const currentExpiresAt = getStoredSessionExpiresAt();

      localStorage.setItem(TOKEN_KEY, token);

      if (token !== currentToken || !currentExpiresAt) {
        localStorage.setItem(SESSION_EXPIRES_AT_KEY, String(Date.now() + getSessionDurationMs()));
      }
    }

    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  clearSession,
};
